import Vue from 'vue'
import { find, cloneDeep, keyBy } from 'lodash'

function sortLayout(state) {
    state.layout = state.layout.sort(function (a, b) {
        return b.product_number - a.product_number
    })
}

/**
 * Модуль Vuex для работы с планограммой и продукцией автомата.
 *
 * Для начала работы необходимо вызвать мутацию init, на вход которой подать layout и capacity.
 */
export default {
    namespaced: true,

    state: {
        layout: [],
        capacity: []
    },

    getters: {
        /**
         * Таблица продуктов и рецептов.
         */
        products: state => state.layout.map(product => {
            const component = find(state.capacity, { product_number: product.product_number, id: product.components[0].id }) || { max: 0, critical: 0 }

            const { max, critical } = component

            return {
                name: product.name,
                product_number: product.product_number,
                is_composite: product.is_composite,
                max,
                critical
            }
        }),

        /**
         * Таблица ингредиентов
         */
        components(state) {
            return state.capacity.filter(component => !component.is_product)
        },

        loading(state) {
            return state.capacity
        },

        /**
         * Данные, готовые к отправке на бекенд для сохранения.
         */
        normalized: state => ({
            layout: state.layout.map(product => {
                return {
                    id: product.id,
                    product_number: product.product_number
                }
            }),

            capacity: state.capacity.map(component => {
                return {
                    id: component.id,
                    critical: component.critical,
                    max: component.max,
                    product_number: component.product_number
                }
            })
        })
    },

    mutations: {
        REBUILD_CAPACITY(state) {
            const capacity = keyBy(state.capacity, component => `${component.id}_${component.product_number}`)

            let components = []
            let ids = []

            for (let i = 0; i < state.layout.length; i++) {
                const product = state.layout[i]

                for (let j = 0; j < product.components.length; j++) {
                    let id = product.components[j].id

                    let key = `${id}_${product.is_composite ? null : product.product_number}`
                    let component = capacity[key]

                    if (!component) {
                        component = { ...product.components[j], max: 0, critical: 0 }
                    }

                    if (component.is_product === false) {
                        if (ids.includes(id)) {
                            continue
                        }

                        ids.push(id)
                    }

                    components.push(component)
                }
            }

            state.capacity = components
        },

        /**
         * Метод с которого начинается работа с этим модулем стора.
         * На вход подается таблица продуктов и рецептов (layout) и вместимости ингредов (capacity).
         */
        init(state, { layout, capacity }) {
            state.layout = layout
            state.capacity = capacity
        },

        flush(state) {
            state.layout = []
            state.capacity = []
        },

        /**
         * Обработчик формы добавления продукта/рецепта
         */
        add(state, { product, product_number, max = 0 }) {
            state.layout.push({ ...cloneDeep(product), product_number })

            product.components.forEach(component => {
                if (!component.is_product && state.capacity.findIndex(с => с.id === component.id) !== -1) {
                    return
                }

                state.capacity.push({
                    id: component.id,
                    product_number: component.is_product ? product_number : null,
                    name: component.name,
                    is_product: component.is_product,
                    unit: component.unit,
                    max: max || 0,
                    critical: 0
                })
            })

            sortLayout(state)
        },

        REMOVE(state, index) {
            state.layout.splice(index, 1)
        },

        /**
         * Обработчик изменений в таблице ингредиентов.
         */
        updateRecipeComponent(state, { id, max = 0 }) {
            const component = find(state.capacity, { id, is_product: false })

            if (!component) {
                return
            }

            component.max = max
        },

        /**
         * Обработчик изменений в таблице продуктов и рецептов.
         */
        updateProduct(state, { index, product_number, max }) {
            const product = cloneDeep(state.layout[index])

            if (!product) {
                return
            }

            // Для продукта можно менять вместимость, для рецепта - нет.
            // Она меняется в его продуктовом ингреде (product_number там тоже есть, бекенд требует).
            if (product.is_composite === false) {
                const component = find(state.capacity, { product_number: product.product_number, id: product.components[0].id })

                if (component) {
                    component.max = max
                    component.product_number = product_number
                }
            }

            product.product_number = product_number
            Vue.set(state.layout, index, product)
            sortLayout(state)
        }
    },

    actions: {
        /**
         * Удаляет запись из таблицы продуктов и рецептов по ее индексу.
         */
        remove({ commit }, index) {
            commit('REMOVE', index)
            commit('REBUILD_CAPACITY')
        }
    }
}
